import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { notification } from "antd";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { normalizeLanguage } from "../../services/language";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import { buildFriendInviteLink } from "../../utils/friendReferral";
import "../../components/lobby/lobbyModern.css";
import "./puzzlesV2.css";
import "./friendsV2.css";

// Página Amigos con el diseño Clásico (diseño 1, elegido por Yosdel el 2026-10-18).
// Datos reales de GET /friends/hub (amigos, solicitudes, retos, Invita y gana); búsqueda por
// nombre o código en GET /friends/find; retos en /friends/challenges (la partida rápida usa la
// invitación de siempre, /friends/invite).

const MODES = [
  { id: "free", es: "Ciencia", en: "Science" },
  { id: "cienzar", es: "Cienzar", en: "Cienzar" },
  { id: "sciencevsazar", es: "Contrazar", en: "Contrazar" },
  { id: "dice", es: "Azar", en: "Chance" },
];
const TIMES = [3, 5, 10];
const DEFAULT_STAKES = [50, 100, 500];
const TABS = ["friends", "requests", "challenges", "invite"];
const AVATAR_TONES = ["g1", "g2", "g3", "g4", "g5", "g6"];

const CHALLENGE_TYPES = [
  { id: "quick", icon: "⚡", es: ["Partida rápida", "Elige modo y tiempo. Empieza al aceptar."], en: ["Quick game", "Pick mode and time. Starts on accept."] },
  { id: "bet", icon: "🪙", es: ["Reto con apuesta", "Los dos ponen monedas. El ganador se lo lleva todo."], en: ["Coin bet", "Both stake coins. Winner takes all."] },
  { id: "rematch", icon: "🔁", es: ["Revancha", "La misma partida de la última vez, turnos cambiados."], en: ["Rematch", "Same game as last time, turns swapped."] },
  { id: "puzzle", icon: "🧩", es: ["Duelo de puzzle", "El mismo puzzle para los dos. Gana el más rápido."], en: ["Puzzle duel", "Same puzzle for both. Fastest wins."] },
  { id: "turns", icon: "📅", es: ["Por turnos", "Un día para cada jugada. Juega cuando puedas."], en: ["Turn-based", "One day per move. Play when you can."], soon: true },
  { id: "tournament", icon: "🏆", es: ["Mini torneo", "Invita a varios amigos a un torneo privado."], en: ["Mini tournament", "Invite several friends to a private tournament."] },
];

const modeName = (id, language) => {
  const entry = MODES.find((item) => item.id === String(id || "").toLowerCase());
  return entry ? (language === "es" ? entry.es : entry.en) : "";
};

const formatNumber = (value, language) =>
  Math.max(0, Math.round(Number(value) || 0)).toLocaleString(language === "es" ? "es-ES" : "en-US");

const initialOf = (name) => String(name || "?").trim().charAt(0).toUpperCase() || "?";
const toneOf = (code) => {
  const digits = String(code || "").replace(/\D/g, "");
  return AVATAR_TONES[(Number(digits) || 0) % AVATAR_TONES.length];
};

const timeAgo = (date, language) => {
  const ms = Date.now() - new Date(date || 0).valueOf();
  if (!date || !Number.isFinite(ms) || ms < 0) return "";
  const min = Math.floor(ms / 60000);
  const es = language === "es";
  if (min < 1) return es ? "hace un momento" : "just now";
  if (min < 60) return es ? `hace ${min} min` : `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return es ? `hace ${h} ${h === 1 ? "hora" : "horas"}` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return es ? `hace ${d} ${d === 1 ? "día" : "días"}` : `${d}d ago`;
};

const highlight = (text, query) => {
  const value = String(text || "");
  const q = String(query || "").trim();
  if (!q) return value;
  const index = value.toLowerCase().indexOf(q.toLowerCase());
  if (index < 0) return value;
  return (
    <>
      {value.slice(0, index)}
      <mark>{value.slice(index, index + q.length)}</mark>
      {value.slice(index + q.length)}
    </>
  );
};

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const Avatar = ({ name, code, status, small }) => (
  <div className={`xfr-av ${toneOf(code)}${small ? " is-small" : ""}`} aria-hidden="true">
    {initialOf(name)}
    {status ? <span className={`xfr-dot is-${status}`} /> : null}
  </div>
);

const Friends = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext() || {};
  const routeActions = useModernRouteActions();
  const language = normalizeLanguage(outletContext.language || "es");
  const isAdmin = Boolean(outletContext.hasAdminMenuAccess);
  const refreshSocialAlert = outletContext.refreshSocialAlert;
  const t = useCallback((es, en) => (language === "es" ? es : en), [language]);

  const initialTab = useMemo(() => {
    const requested = String(new URLSearchParams(location.search).get("tab") || "").toLowerCase();
    return TABS.includes(requested) ? requested : "friends";
  }, [location.search]);

  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState(initialTab);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");
  const [challengeType, setChallengeType] = useState("quick");
  const [mode, setMode] = useState("cienzar");
  const [minutes, setMinutes] = useState(5);
  const [stake, setStake] = useState(100);
  const [tournamentCodes, setTournamentCodes] = useState([]);
  const [createdTournament, setCreatedTournament] = useState(null);
  const [busy, setBusy] = useState("");
  const outgoingInviteStateRef = useRef(new Map());
  const searchBoxRef = useRef(null);
  const inviteCardRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => setTab(initialTab), [initialTab]);

  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active", "xfr-page-active");
    return () => document.body.classList.remove("xima-lobby-modern-active", "xfr-page-active");
  }, []);

  const loadHub = useCallback(
    async (silent = false) => {
      try {
        const response = await axios.get("/friends/hub");
        setHub(response?.data || null);
        setLoadError("");
      } catch (error) {
        if (!silent) setLoadError(errorMessage(error, t("No se pudo cargar Amigos.", "Could not load Friends.")));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const refreshAll = useCallback(() => {
    loadHub(true);
    if (typeof refreshSocialAlert === "function") refreshSocialAlert().catch(() => {});
  }, [loadHub, refreshSocialAlert]);

  useEffect(() => {
    loadHub(false);
  }, [loadHub]);

  const hasOutgoingPendingInvite = Boolean(
    hub?.invites?.outgoing?.some((invite) => invite.status === "pending")
  );
  useEffect(() => {
    const id = window.setInterval(() => loadHub(true), hasOutgoingPendingInvite ? 3000 : 8000);
    return () => window.clearInterval(id);
  }, [hasOutgoingPendingInvite, loadHub]);

  // Partida rápida: cuando el amigo acepta, quien invitó entra solo (como antes).
  useEffect(() => {
    const outgoing = hub?.invites?.outgoing || [];
    let joinId = "";
    const next = new Map();
    outgoing.forEach((invite) => {
      next.set(invite.id, invite.status);
      const previous = outgoingInviteStateRef.current.get(invite.id);
      if (!joinId && previous === "pending" && invite.status === "accepted" && invite.tournamentId) {
        joinId = invite.tournamentId;
      }
    });
    outgoingInviteStateRef.current = next;
    if (joinId) {
      notification.success({ message: t("Tu amigo aceptó", "Your friend accepted"), description: t("Entrando a la partida…", "Entering the game…") });
      navigate(`/game/${joinId}`);
    }
  }, [hub, navigate, t]);

  // Retos con partida (apuesta / revancha): el servidor avisa a quien retó cuando el otro acepta.
  useEffect(() => {
    let socket = null;
    let timer = null;
    const onStart = (payload) => {
      const tournamentId = String(payload?.tournamentId || "");
      if (!tournamentId) return;
      notification.success({
        message: t("¡Reto aceptado!", "Challenge accepted!"),
        description: t("Entrando a la partida…", "Entering the game…"),
      });
      navigate(`/game/${tournamentId}`);
    };
    const onNotice = () => loadHub(true);
    const attach = () => {
      const candidate = window.__ximaSocket || window.socket || null;
      if (!candidate || candidate === socket || typeof candidate.on !== "function") return;
      if (socket) {
        socket.off?.("FRIEND_CHALLENGE_START", onStart);
        socket.off?.("FRIEND_NOTICE", onNotice);
      }
      socket = candidate;
      socket.on("FRIEND_CHALLENGE_START", onStart);
      socket.on("FRIEND_NOTICE", onNotice);
    };
    attach();
    timer = window.setInterval(attach, 1500);
    return () => {
      window.clearInterval(timer);
      if (socket) {
        socket.off?.("FRIEND_CHALLENGE_START", onStart);
        socket.off?.("FRIEND_NOTICE", onNotice);
      }
    };
  }, [loadHub, navigate, t]);

  // Búsqueda mientras se escribe (nombre parcial o código XIMA-1234).
  useEffect(() => {
    const q = query.trim();
    const isCode = /^(xima-?)?\d{4,6}$/i.test(q);
    if (!isCode && q.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    let cancelled = false;
    const id = window.setTimeout(async () => {
      try {
        const response = await axios.get("/friends/find", { params: { q } });
        if (!cancelled) setResults(Array.isArray(response?.data?.results) ? response.data.results : []);
      } catch (error) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [query]);

  useEffect(() => {
    const onDown = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const friends = useMemo(() => hub?.friends || [], [hub]);
  const config = hub?.config || {};
  const stakes = Array.isArray(config.betStakes) && config.betStakes.length ? config.betStakes : DEFAULT_STAKES;
  const inviterReward = Number(config.inviterReward) || 0;
  const inviteeReward = Number(config.inviteeReward) || 0;
  const bonusInvites = Number(config.bonusInvites) || 5;
  const bonusReward = Number(config.bonusReward) || 0;
  const me = hub?.me || null;
  const challenges = hub?.challenges || { incoming: [], outgoing: [], active: [], recent: [] };
  const incomingQuick = hub?.invites?.incoming || [];
  const outgoingQuick = (hub?.invites?.outgoing || []).filter((invite) => invite.status === "pending");
  const requestsIncoming = hub?.requests?.incoming || [];
  const requestsOutgoing = hub?.requests?.outgoing || [];
  const referral = hub?.referral || { list: [], paidCount: 0, earned: 0 };
  const inviteLink = buildFriendInviteLink(me?.playerCode);
  const receivedCount = (challenges.incoming?.length || 0) + incomingQuick.length;

  const selectedFriend = useMemo(
    () => friends.find((entry) => entry.user?.playerCode === selectedCode) || null,
    [friends, selectedCode]
  );

  useEffect(() => {
    if (!selectedCode && friends.length) setSelectedCode(friends[0].user?.playerCode || "");
  }, [friends, selectedCode]);

  useEffect(() => {
    if (challengeType === "tournament" && selectedCode) {
      setTournamentCodes((current) => (current.length ? current : [selectedCode]));
    }
  }, [challengeType, selectedCode]);

  useEffect(() => {
    if (!stakes.includes(stake)) setStake(stakes[Math.min(1, stakes.length - 1)]);
  }, [stake, stakes]);

  const run = async (key, action) => {
    setBusy(key);
    try {
      return await action();
    } finally {
      setBusy("");
    }
  };

  const pickFriend = (code) => {
    setSelectedCode(code);
    setCreatedTournament(null);
    if (panelRef.current && window.innerWidth < 1100) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sendRequest = (code) =>
    run(`req-${code}`, async () => {
      try {
        await axios.post("/friends/request", { targetPlayerCode: code });
        notification.success({ message: t("Solicitud enviada", "Request sent"), description: t("Cuando acepte, ya son amigos.", "Once accepted, you are friends.") });
        setResults((list) => list.map((item) => (item.user?.playerCode === code ? { ...item, relation: "outgoing_pending" } : item)));
        refreshAll();
      } catch (error) {
        notification.error({ message: t("No se pudo enviar", "Could not send"), description: errorMessage(error, "") });
      }
    });

  const answerRequest = (id, action) =>
    run(`ans-${id}`, async () => {
      try {
        await axios.post(`/friends/request/${id}/${action}`);
        if (action === "accept") notification.success({ message: t("¡Ya son amigos!", "You are now friends!") });
        refreshAll();
      } catch (error) {
        notification.error({ message: t("No se pudo completar", "Could not complete"), description: errorMessage(error, "") });
      }
    });

  const acceptFromSearch = (code) => {
    const request = requestsIncoming.find((item) => item.fromUser?.playerCode === code);
    if (request) answerRequest(request.id, "accept");
  };

  const watchFriend = (code) =>
    run(`watch-${code}`, async () => {
      try {
        const response = await axios.get("/friends/join", { params: { friendPlayerCode: code } });
        const tournamentId = String(response?.data?.tournamentId || "");
        const roomId = String(response?.data?.roomId || "");
        if (!tournamentId || !roomId) throw new Error(t("La partida ya terminó.", "The game is over."));
        navigate(`/game/${encodeURIComponent(tournamentId)}?autoplay=1&spectator=1&roomId=${encodeURIComponent(roomId)}`);
      } catch (error) {
        notification.error({ message: t("No se puede mirar", "Cannot watch"), description: errorMessage(error, "") });
      }
    });

  const sendChallenge = () => {
    if (challengeType === "tournament") {
      return run("challenge", async () => {
        try {
          const response = await axios.post("/friends/challenges", {
            type: "tournament",
            friendPlayerCodes: tournamentCodes,
            mode,
            havingTime: minutes,
          });
          setCreatedTournament(response?.data || null);
          notification.success({
            message: t("Mini torneo creado", "Mini tournament created"),
            description: t(`Invitaste a ${response?.data?.invited || 0} amigos.`, `You invited ${response?.data?.invited || 0} friends.`),
          });
          refreshAll();
        } catch (error) {
          notification.error({ message: t("No se pudo crear", "Could not create"), description: errorMessage(error, "") });
        }
      });
    }
    if (!selectedFriend) return undefined;
    const friendPlayerCode = selectedFriend.user.playerCode;
    return run("challenge", async () => {
      try {
        if (challengeType === "quick") {
          await axios.post("/friends/invite", { friendPlayerCode, mode, havingTime: minutes });
        } else {
          await axios.post("/friends/challenges", {
            type: challengeType,
            friendPlayerCode,
            mode,
            havingTime: minutes,
            stake,
          });
        }
        notification.success({
          message: t("Reto enviado", "Challenge sent"),
          description: t(
            `Le llegó un aviso a ${selectedFriend.user.displayName}.`,
            `${selectedFriend.user.displayName} got a notification.`
          ),
        });
        refreshAll();
      } catch (error) {
        notification.error({ message: t("No se pudo enviar el reto", "Could not send the challenge"), description: errorMessage(error, "") });
      }
    });
  };

  const acceptQuick = (id) =>
    run(`qa-${id}`, async () => {
      try {
        const response = await axios.post(`/friends/invite/${id}/accept`);
        refreshAll();
        if (response?.data?.tournamentId) navigate(`/game/${response.data.tournamentId}`);
      } catch (error) {
        notification.error({ message: t("No se pudo aceptar", "Could not accept"), description: errorMessage(error, "") });
      }
    });

  const declineQuick = (id, action = "deny") =>
    run(`qd-${id}`, async () => {
      try {
        await axios.post(`/friends/invite/${id}/${action}`);
        refreshAll();
      } catch (error) {
        notification.error({ message: t("No se pudo completar", "Could not complete"), description: errorMessage(error, "") });
      }
    });

  const respondChallenge = (challenge, accept) =>
    run(`ch-${challenge.id}`, async () => {
      try {
        const response = await axios.post(`/friends/challenges/${challenge.id}/${accept ? "accept" : "decline"}`);
        refreshAll();
        if (!accept) return;
        const data = response?.data || {};
        if (data.puzzleId) navigate(`/game/${data.puzzleId}?autoplay=1&puzzle=1`);
        else if (data.tournamentId) navigate(`/game/${data.tournamentId}`);
      } catch (error) {
        notification.error({ message: t("No se pudo completar", "Could not complete"), description: errorMessage(error, "") });
        refreshAll();
      }
    });

  const cancelChallenge = (challenge) =>
    run(`cc-${challenge.id}`, async () => {
      try {
        await axios.post(`/friends/challenges/${challenge.id}/cancel`);
        refreshAll();
      } catch (error) {
        notification.error({ message: t("No se pudo cancelar", "Could not cancel"), description: errorMessage(error, "") });
      }
    });

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notification.success({ message: label || t("Copiado", "Copied"), description: text });
    } catch (error) {
      notification.info({ message: t("Cópialo a mano", "Copy it manually"), description: text });
    }
  };

  const inviteMessage = t(
    `¡Juega XIMA conmigo! Crea tu cuenta con mi enlace y te regalan ${inviteeReward} monedas: ${inviteLink}`,
    `Play XIMA with me! Sign up with my link and get ${inviteeReward} coins: ${inviteLink}`
  );
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`, "_blank", "noopener");
  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(t("Te invito a XIMA", "Join me on XIMA"))}&body=${encodeURIComponent(inviteMessage)}`;
  };
  const shareContacts = async () => {
    try {
      if (navigator.contacts && typeof navigator.contacts.select === "function") {
        const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: false });
        const contact = Array.isArray(picked) ? picked[0] : null;
        const tel = String(contact?.tel?.[0] || "").replace(/[^\d+]/g, "");
        const email = String(contact?.email?.[0] || "");
        if (tel) {
          window.open(`https://wa.me/${tel.replace(/^\+/, "")}?text=${encodeURIComponent(inviteMessage)}`, "_blank", "noopener");
          return;
        }
        if (email) {
          window.location.href = `mailto:${email}?subject=${encodeURIComponent(t("Te invito a XIMA", "Join me on XIMA"))}&body=${encodeURIComponent(inviteMessage)}`;
          return;
        }
        if (contact) return;
      }
      if (navigator.share) {
        await navigator.share({ title: "XIMA", text: inviteMessage, url: inviteLink });
        return;
      }
      copyText(inviteLink, t("Enlace copiado", "Link copied"));
    } catch (error) {
      if (error?.name !== "AbortError") copyText(inviteLink, t("Enlace copiado", "Link copied"));
    }
  };

  const goInvite = () => {
    setSearchOpen(false);
    setTab("invite");
    window.setTimeout(() => inviteCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const statusLine = (entry) => {
    if (entry.status === "in_match") {
      const name = modeName(entry.activeMatch?.mode, language);
      return { cls: "is-playing", text: name ? t(`Jugando ${name}`, `Playing ${name}`) : t("En partida", "In a game") };
    }
    if (entry.status === "online") return { cls: "is-online", text: t("En línea", "Online") };
    const ago = timeAgo(entry.h2h?.friendLastPlayedAt, language);
    return { cls: "is-offline", text: ago ? t(`Jugó ${ago}`, `Played ${ago}`) : t("Desconectado", "Offline") };
  };

  const h2hLine = (h2h) => {
    if (!h2h || !h2h.games) return { score: t("Tú —", "You —"), last: t("Nunca han jugado", "Never played") };
    const last =
      h2h.lastResult === "win"
        ? t("Ganaste la última", "You won the last one")
        : h2h.lastResult === "loss"
        ? t("Te ganó la última", "Lost the last one")
        : t("Empate en la última", "Last one was a draw");
    const draws = h2h.draws ? ` · ${h2h.draws} ${h2h.draws === 1 ? t("empate", "draw") : t("empates", "draws")}` : "";
    return { score: `${t("Tú", "You")} ${h2h.wins} – ${h2h.losses}`, last: `${last}${draws}` };
  };

  const kpis = hub?.kpis || {};
  const selectedName = selectedFriend?.user?.displayName || "";
  const lastGame = selectedFriend?.h2h?.lastGame || null;
  const canRematch = Boolean(lastGame?.rematchable);
  const walletBalance = Number(me?.walletBalance) || 0;
  const typeInfo = CHALLENGE_TYPES.find((entry) => entry.id === challengeType) || CHALLENGE_TYPES[0];
  const needsMode = ["quick", "bet", "tournament"].includes(challengeType);
  const challengeDisabled =
    busy === "challenge" ||
    typeInfo.soon ||
    (challengeType === "tournament" ? tournamentCodes.length === 0 : !selectedFriend) ||
    (challengeType === "rematch" && !canRematch) ||
    (challengeType === "bet" && walletBalance < stake);

  const sendLabel = () => {
    if (challengeType === "tournament") {
      return t(`Crear mini torneo con ${tournamentCodes.length} ${tournamentCodes.length === 1 ? "amigo" : "amigos"}`, `Create mini tournament with ${tournamentCodes.length}`);
    }
    if (challengeType === "bet") return t(`Enviar reto de ${formatNumber(stake, language)} 🪙 a ${selectedName}`, `Send ${stake} 🪙 bet to ${selectedName}`);
    if (challengeType === "rematch") return t(`Pedir la revancha a ${selectedName}`, `Ask ${selectedName} for a rematch`);
    if (challengeType === "puzzle") return t(`Retar a ${selectedName} a un duelo`, `Challenge ${selectedName} to a duel`);
    return t(`Invitar a ${selectedName} a jugar`, `Invite ${selectedName} to play`);
  };

  const sendNote = () => {
    const hours = Number(config.challengeTtlHours) || 24;
    if (challengeType === "bet") {
      if (walletBalance < stake) return t(`Tienes ${formatNumber(walletBalance, language)} 🪙: no alcanza para esta apuesta.`, `You have ${walletBalance} 🪙: not enough for this bet.`);
      return t(`Le llega un aviso. Si no acepta en ${hours} h, te devolvemos las monedas. Empate = se devuelven.`, `They get a notification. If not accepted in ${hours} h, you get your coins back.`);
    }
    if (challengeType === "rematch") {
      return canRematch
        ? t(`${modeName(lastGame.mode, language)} · ${lastGame.havingTime} min, como la última vez. Esta vez empieza el otro.`, `${modeName(lastGame.mode, language)} · ${lastGame.havingTime} min, like last time. The other player starts.`)
        : t("Todavía no han jugado una partida para pedir la revancha.", "You have not played a game together yet.");
    }
    if (challengeType === "puzzle") {
      return t(`Le toca el mismo puzzle a los dos (uno que ninguno ha resuelto). Tienen ${Number(config.puzzleDuelHours) || 24} h; cuenta el primer intento.`, "Same puzzle for both; first attempt counts.");
    }
    if (challengeType === "tournament") {
      return t("Torneo con contraseña: solo entran los amigos que invites. Les llega un aviso.", "Password-protected: only invited friends can join.");
    }
    if (challengeType === "turns") return t("Las partidas por turnos llegan pronto.", "Turn-based games are coming soon.");
    return t("Le llega un aviso. Cuando acepte, entran los dos a la partida.", "They get a notification. When they accept, you both join.");
  };

  const renderSearch = () => {
    const showDrop = searchOpen && query.trim().length > 0;
    return (
      <div className="xfr-search-wrap" ref={searchBoxRef}>
        <label className="xfr-search">
          <span aria-hidden="true">🔍</span>
          <input
            type="search"
            value={query}
            placeholder={t("Busca por nombre o código", "Search by name or code")}
            onChange={(event) => {
              setQuery(event.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            aria-label={t("Buscar jugadores", "Search players")}
          />
          <kbd>{t("nombre o código XIMA-1234", "name or code XIMA-1234")}</kbd>
        </label>
        {showDrop ? (
          <div className="xfr-drop">
            <div className="xfr-label">{t("JUGADORES", "PLAYERS")}</div>
            {searching && !results.length ? <div className="xfr-drop-empty">{t("Buscando…", "Searching…")}</div> : null}
            {!searching && !results.length ? (
              <div className="xfr-drop-empty">
                {query.trim().length < 2 ? t("Escribe al menos 2 letras.", "Type at least 2 letters.") : t("Nadie con ese nombre.", "Nobody with that name.")}
              </div>
            ) : null}
            {results.map((item) => {
              const code = item.user?.playerCode || "";
              const name = item.user?.displayName || "";
              const relation = item.relation;
              const dot = item.status === "in_match" ? "playing" : item.status === "online" ? "online" : "offline";
              return (
                <div key={code} className="xfr-drop-row">
                  <Avatar name={name} code={code} status={dot} small />
                  <div className="xfr-drop-name">
                    <b>{highlight(name, query)}</b>
                    <span>
                      · {code}
                      {relation === "friend" ? ` · ${t("ya es tu amigo", "already your friend")}` : ""}
                      {relation === "outgoing_pending" ? ` · ${t("solicitud enviada", "request sent")}` : ""}
                      {relation === "incoming_pending" ? ` · ${t("te envió solicitud", "sent you a request")}` : ""}
                    </span>
                  </div>
                  {relation === "friend" ? (
                    <button type="button" className="xpz-btn is-gold" onClick={() => { pickFriend(code); setSearchOpen(false); }}>
                      ⚔ {t("Retar", "Challenge")}
                    </button>
                  ) : relation === "incoming_pending" ? (
                    <button type="button" className="xpz-btn is-claim" disabled={Boolean(busy)} onClick={() => acceptFromSearch(code)}>
                      ✓ {t("Aceptar", "Accept")}
                    </button>
                  ) : relation === "outgoing_pending" ? (
                    <button type="button" className="xpz-btn is-ghost" disabled>
                      {t("Enviada", "Sent")}
                    </button>
                  ) : (
                    <button type="button" className="xpz-btn is-ghost" disabled={busy === `req-${code}`} onClick={() => sendRequest(code)}>
                      ➕ {t("Agregar", "Add")}
                    </button>
                  )}
                </div>
              );
            })}
            <button type="button" className="xfr-drop-foot" onClick={goInvite}>
              {t("¿No lo encuentras?", "Can't find them?")}{" "}
              <b>
                {t(`Invítalo a XIMA y gana ${formatNumber(inviterReward, language)} 🪙 →`, `Invite them and earn ${inviterReward} 🪙 →`)}
              </b>
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderFriends = () => {
    if (!friends.length) {
      return (
        <div className="xfr-empty">
          <b>{t("Todavía no tienes amigos en XIMA.", "No friends yet.")}</b>
          <span>{t("Búscalos arriba por su nombre o invítalos con tu enlace.", "Search above by name or invite them with your link.")}</span>
          <button type="button" className="xpz-btn is-gold" onClick={goInvite}>
            🎁 {t("Invitar amigos", "Invite friends")}
          </button>
        </div>
      );
    }
    return friends.map((entry) => {
      const code = entry.user?.playerCode || "";
      const name = entry.user?.displayName || "";
      const status = statusLine(entry);
      const record = h2hLine(entry.h2h);
      const dot = entry.status === "in_match" ? "playing" : entry.status === "online" ? "online" : "offline";
      const canWatch = entry.status === "in_match" && entry.activeMatch?.spectatable;
      return (
        <div key={code} className={`xfr-card xfr-friend${selectedCode === code ? " is-selected" : ""}`}>
          <Avatar name={name} code={code} status={dot} />
          <button type="button" className="xfr-friend-info" onClick={() => pickFriend(code)}>
            <div className="xfr-friend-name">
              {name} <span>· {code}</span>
            </div>
            <div className={`xfr-status ${status.cls}`}>● {status.text}</div>
          </button>
          <div className="xfr-h2h">
            <b>{record.score}</b>
            <span>{record.last}</span>
          </div>
          <button type="button" className="xpz-btn is-ghost xfr-icon-btn" title={t("Ver perfil", "View profile")} aria-label={t("Ver perfil", "View profile")} onClick={() => navigate(`/game/player/${encodeURIComponent(code)}`)}>
            👤
          </button>
          {canWatch ? (
            <button type="button" className="xpz-btn is-ghost" disabled={busy === `watch-${code}`} onClick={() => watchFriend(code)}>
              👁 {t("Mirar", "Watch")}
            </button>
          ) : (
            <button type="button" className="xpz-btn is-gold" onClick={() => pickFriend(code)}>
              ⚔ {t("Retar", "Challenge")}
            </button>
          )}
        </div>
      );
    });
  };

  const userRow = (key, user, meta, actions) => (
    <div key={key} className="xfr-card xfr-row">
      <Avatar name={user?.displayName} code={user?.playerCode} />
      <div className="xfr-row-info">
        <div className="xfr-friend-name">
          {user?.displayName || t("Jugador", "Player")} <span>· {user?.playerCode}</span>
        </div>
        <div className="xfr-row-meta">{meta}</div>
      </div>
      <div className="xfr-row-actions">{actions}</div>
    </div>
  );

  const renderRequests = () => (
    <>
      <div className="xfr-label">{t("RECIBIDAS", "RECEIVED")}</div>
      {requestsIncoming.length ? (
        requestsIncoming.map((request) =>
          userRow(
            request.id,
            request.fromUser,
            t(`Quiere ser tu amigo · ${timeAgo(request.createdAt, language)}`, `Wants to be your friend · ${timeAgo(request.createdAt, language)}`),
            <>
              <button type="button" className="xpz-btn is-claim" disabled={busy === `ans-${request.id}`} onClick={() => answerRequest(request.id, "accept")}>
                ✓ {t("Aceptar", "Accept")}
              </button>
              <button type="button" className="xpz-btn is-ghost" disabled={busy === `ans-${request.id}`} onClick={() => answerRequest(request.id, "deny")}>
                {t("Rechazar", "Decline")}
              </button>
            </>
          )
        )
      ) : (
        <div className="xfr-empty is-small">{t("No tienes solicitudes nuevas.", "No new requests.")}</div>
      )}
      <div className="xfr-label">{t("ENVIADAS", "SENT")}</div>
      {requestsOutgoing.length ? (
        requestsOutgoing.map((request) =>
          userRow(
            request.id,
            request.toUser,
            t(`Esperando respuesta · ${timeAgo(request.createdAt, language)}`, `Waiting · ${timeAgo(request.createdAt, language)}`),
            <button type="button" className="xpz-btn is-ghost" disabled={busy === `ans-${request.id}`} onClick={() => answerRequest(request.id, "cancel")}>
              {t("Cancelar", "Cancel")}
            </button>
          )
        )
      ) : (
        <div className="xfr-empty is-small">{t("No has enviado solicitudes.", "No sent requests.")}</div>
      )}
    </>
  );

  const challengeTitle = (challenge) => {
    const typeEntry = CHALLENGE_TYPES.find((entry) => entry.id === challenge.type);
    return typeEntry ? (language === "es" ? typeEntry.es[0] : typeEntry.en[0]) : "";
  };

  const challengeDetails = (challenge) => {
    const parts = [];
    if (challenge.type === "bet") parts.push(`🪙 ${formatNumber(challenge.stake, language)} ${t("cada uno", "each")}`);
    if (challenge.modeName) parts.push(modeName(challenge.mode, language));
    if (challenge.havingTime) parts.push(`${challenge.havingTime} min`);
    if (challenge.type === "rematch") parts.push(t("turnos cambiados", "turns swapped"));
    if (challenge.type === "puzzle") parts.push(challenge.puzzleName || t("puzzle sorpresa", "surprise puzzle"));
    if (challenge.type === "tournament") parts.push(t("torneo privado", "private tournament"));
    return parts.join(" · ");
  };

  const outcomeText = (challenge) => {
    if (challenge.status === "declined") return t("Rechazado", "Declined");
    if (challenge.status === "canceled") return t("Cancelado", "Canceled");
    if (challenge.status === "expired") return t("Caducó (24 h)", "Expired");
    if (challenge.status === "aborted") return t("No se jugó · monedas devueltas", "Not played · refunded");
    if (challenge.type === "tournament") return t("Se unió al torneo", "Joined the tournament");
    if (challenge.outcome === "win") return challenge.payout ? t(`Ganaste · +${formatNumber(challenge.payout, language)} 🪙`, `You won · +${challenge.payout} 🪙`) : t("Ganaste", "You won");
    if (challenge.outcome === "loss") return challenge.stake ? t(`Perdiste · −${formatNumber(challenge.stake, language)} 🪙`, `You lost · −${challenge.stake} 🪙`) : t("Perdiste", "You lost");
    if (challenge.outcome === "draw") return t("Empate", "Draw");
    if (challenge.outcome === "refund") return t("Sin ganador · devuelto", "No winner · refunded");
    return "";
  };

  const duelTimes = (challenge) => {
    const fmt = (side) => (!side ? "—" : side.solved ? `${(side.timeMs / 1000).toFixed(1)} s` : side.finished ? t("falló", "failed") : side.started ? t("jugando", "playing") : t("sin jugar", "not played"));
    return `${t("Tú", "You")}: ${fmt(challenge.duel?.me)} · ${challenge.other?.displayName}: ${fmt(challenge.duel?.other)}`;
  };

  const renderChallenges = () => (
    <>
      <div className="xfr-label">{t("RECIBIDOS", "RECEIVED")}</div>
      {!receivedCount ? <div className="xfr-empty is-small">{t("No tienes retos pendientes.", "No pending challenges.")}</div> : null}
      {incomingQuick.map((invite) =>
        userRow(
          `qi-${invite.id}`,
          invite.fromUser,
          <>
            <b className="xfr-tag is-gold">⚡ {t("Partida rápida", "Quick game")}</b> {modeName(invite.mode, language)} · {invite.havingTime} min · {timeAgo(invite.createdAt, language)}
          </>,
          <>
            <button type="button" className="xpz-btn is-claim" disabled={busy === `qa-${invite.id}`} onClick={() => acceptQuick(invite.id)}>
              ✓ {t("Jugar", "Play")}
            </button>
            <button type="button" className="xpz-btn is-ghost" disabled={busy === `qd-${invite.id}`} onClick={() => declineQuick(invite.id)}>
              {t("Rechazar", "Decline")}
            </button>
          </>
        )
      )}
      {(challenges.incoming || []).map((challenge) =>
        userRow(
          `ci-${challenge.id}`,
          { displayName: challenge.other.displayName, playerCode: challenge.other.playerCode },
          <>
            <b className="xfr-tag is-gold">{CHALLENGE_TYPES.find((e) => e.id === challenge.type)?.icon} {challengeTitle(challenge)}</b> {challengeDetails(challenge)}
            {challenge.type === "bet" && walletBalance < challenge.stake ? (
              <em className="xfr-warn"> · {t(`necesitas ${challenge.stake} 🪙`, `you need ${challenge.stake} 🪙`)}</em>
            ) : null}
          </>,
          <>
            <button
              type="button"
              className="xpz-btn is-claim"
              disabled={busy === `ch-${challenge.id}` || (challenge.type === "bet" && walletBalance < challenge.stake)}
              onClick={() => respondChallenge(challenge, true)}
            >
              ✓ {t("Aceptar", "Accept")}
            </button>
            <button type="button" className="xpz-btn is-ghost" disabled={busy === `ch-${challenge.id}`} onClick={() => respondChallenge(challenge, false)}>
              {t("Rechazar", "Decline")}
            </button>
          </>
        )
      )}

      {challenges.active?.length ? <div className="xfr-label">{t("EN CURSO", "IN PROGRESS")}</div> : null}
      {(challenges.active || []).map((challenge) => {
        const isPuzzle = challenge.type === "puzzle";
        const myDone = isPuzzle && challenge.duel?.me?.finished;
        return userRow(
          `ca-${challenge.id}`,
          { displayName: challenge.other.displayName, playerCode: challenge.other.playerCode },
          <>
            <b className="xfr-tag is-green">{challengeTitle(challenge)}</b> {challengeDetails(challenge)}
            {isPuzzle ? <span className="xfr-sub">{duelTimes(challenge)}</span> : null}
          </>,
          isPuzzle ? (
            myDone ? (
              <span className="xfr-wait">{t(`Esperando a ${challenge.other.displayName}`, `Waiting for ${challenge.other.displayName}`)}</span>
            ) : (
              <button type="button" className="xpz-btn is-gold" onClick={() => navigate(`/game/${challenge.puzzleId}?autoplay=1&puzzle=1`)}>
                🧩 {t("Jugar el puzzle", "Play the puzzle")}
              </button>
            )
          ) : challenge.gameTournamentId ? (
            <button type="button" className="xpz-btn is-gold" onClick={() => navigate(`/game/${challenge.gameTournamentId}`)}>
              ▶ {t("Ir a la partida", "Go to the game")}
            </button>
          ) : null
        );
      })}

      {(challenges.outgoing?.length || outgoingQuick.length) ? <div className="xfr-label">{t("ENVIADOS", "SENT")}</div> : null}
      {outgoingQuick.map((invite) =>
        userRow(
          `qo-${invite.id}`,
          invite.toUser,
          <>
            <b className="xfr-tag">⚡ {t("Partida rápida", "Quick game")}</b> {modeName(invite.mode, language)} · {invite.havingTime} min · {t("esperando", "waiting")}
          </>,
          <button type="button" className="xpz-btn is-ghost" disabled={busy === `qd-${invite.id}`} onClick={() => declineQuick(invite.id, "cancel")}>
            {t("Cancelar", "Cancel")}
          </button>
        )
      )}
      {(challenges.outgoing || []).map((challenge) =>
        userRow(
          `co-${challenge.id}`,
          { displayName: challenge.other.displayName, playerCode: challenge.other.playerCode },
          <>
            <b className="xfr-tag">{challengeTitle(challenge)}</b> {challengeDetails(challenge)} · {t("esperando", "waiting")}
            {challenge.joinCode ? <span className="xfr-sub">{t("Contraseña del torneo", "Tournament password")}: <b>{challenge.joinCode}</b></span> : null}
          </>,
          <>
            {challenge.type === "tournament" && challenge.gameTournamentId ? (
              <button type="button" className="xpz-btn is-gold" onClick={() => navigate(`/game/${challenge.gameTournamentId}`)}>
                🏆 {t("Ir al torneo", "Open")}
              </button>
            ) : null}
            <button type="button" className="xpz-btn is-ghost" disabled={busy === `cc-${challenge.id}`} onClick={() => cancelChallenge(challenge)}>
              {t("Cancelar", "Cancel")}
            </button>
          </>
        )
      )}

      {challenges.recent?.length ? <div className="xfr-label">{t("RESULTADOS", "RESULTS")}</div> : null}
      {(challenges.recent || []).map((challenge) =>
        userRow(
          `cr-${challenge.id}`,
          { displayName: challenge.other.displayName, playerCode: challenge.other.playerCode },
          <>
            <b className="xfr-tag">{challengeTitle(challenge)}</b> {challengeDetails(challenge)}
            {challenge.type === "puzzle" && challenge.status === "finished" ? <span className="xfr-sub">{duelTimes(challenge)}</span> : null}
          </>,
          <span className={`xfr-result is-${challenge.outcome || challenge.status}`}>{outcomeText(challenge)}</span>
        )
      )}
    </>
  );

  const referralStatus = (status) => {
    if (status === "paid") return <span className="xfr-pill is-ok">{t("pagado", "paid")}</span>;
    if (status === "waiting_first_game") return <span className="xfr-pill is-wait">{t("falta 1ª partida", "needs 1st game")}</span>;
    if (status === "blocked") return <span className="xfr-pill is-no">{t("no cuenta (misma red)", "not counted")}</span>;
    return <span className="xfr-pill is-no">{t("no cuenta (sin registrarse)", "not counted (guest)")}</span>;
  };

  const renderInviteList = () => (
    <>
      <div className="xfr-label">{t("TUS INVITADOS", "YOUR INVITES")}</div>
      {referral.list?.length ? (
        referral.list.map((row) => (
          <div key={row.id} className="xfr-ref-row">
            <Avatar name={row.name} code={row.playerCode} small />
            <div className="xfr-row-info">
              <div className="xfr-friend-name">
                {row.name} {row.playerCode ? <span>· {row.playerCode}</span> : null}
              </div>
              <div className="xfr-row-meta">{t(`Llegó ${timeAgo(row.createdAt, language)}`, `Joined ${timeAgo(row.createdAt, language)}`)}</div>
            </div>
            {row.coins ? <b className="xfr-coin">+{formatNumber(row.coins, language)} 🪙</b> : null}
            {referralStatus(row.status)}
          </div>
        ))
      ) : (
        <div className="xfr-empty is-small">
          {t("Aún nadie se ha unido con tu enlace. Compártelo por WhatsApp o correo.", "Nobody joined with your link yet.")}
        </div>
      )}
    </>
  );

  const renderPanel = () => (
    <div className="xfr-card xfr-panel" ref={panelRef}>
      <div className="xfr-sec">
        ⚔ {challengeType === "tournament" ? t("Mini torneo con amigos", "Mini tournament") : selectedName ? t(`Retar a ${selectedName}`, `Challenge ${selectedName}`) : t("Retar a un amigo", "Challenge a friend")}
      </div>
      <div className="xfr-muted">
        {friends.length
          ? t("Elige qué tipo de reto le quieres lanzar.", "Pick the type of challenge.")
          : t("Agrega amigos para poder retarlos.", "Add friends to challenge them.")}
      </div>
      <div className="xfr-types">
        {CHALLENGE_TYPES.map((entry) => {
          const [title, desc] = language === "es" ? entry.es : entry.en;
          return (
            <button
              key={entry.id}
              type="button"
              className={`xfr-type${challengeType === entry.id ? " is-active" : ""}${entry.soon ? " is-soon" : ""}`}
              onClick={() => {
                setChallengeType(entry.id);
                setCreatedTournament(null);
              }}
            >
              <i aria-hidden="true">{entry.icon}</i>
              <b>
                {title} {entry.soon ? <small>{t("Pronto", "Soon")}</small> : null}
              </b>
              <span>{desc}</span>
            </button>
          );
        })}
      </div>

      {challengeType !== "tournament" && friends.length > 1 ? (
        <>
          <div className="xfr-label">{t("AMIGO", "FRIEND")}</div>
          <select className="xfr-select" value={selectedCode} onChange={(event) => setSelectedCode(event.target.value)}>
            {friends.map((entry) => (
              <option key={entry.user.playerCode} value={entry.user.playerCode}>
                {entry.user.displayName} · {entry.user.playerCode}
              </option>
            ))}
          </select>
        </>
      ) : null}

      {challengeType === "tournament" ? (
        <>
          <div className="xfr-label">{t("AMIGOS INVITADOS", "INVITED FRIENDS")}</div>
          <div className="xfr-checks">
            {friends.map((entry) => {
              const code = entry.user.playerCode;
              const checked = tournamentCodes.includes(code);
              return (
                <label key={code} className={`xfr-check${checked ? " is-on" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setTournamentCodes((list) =>
                        checked
                          ? list.filter((item) => item !== code)
                          : list.length >= (Number(config.miniTournamentMaxFriends) || 8)
                          ? list
                          : [...list, code]
                      )
                    }
                  />
                  {entry.user.displayName}
                </label>
              );
            })}
          </div>
        </>
      ) : null}

      {needsMode ? (
        <>
          <div className="xfr-label">{t("MODO", "MODE")}</div>
          <div className="xfr-pills">
            {MODES.map((entry) => (
              <button key={entry.id} type="button" className={`xfr-pillbtn${mode === entry.id ? " is-active" : ""}`} onClick={() => setMode(entry.id)}>
                {language === "es" ? entry.es : entry.en}
              </button>
            ))}
          </div>
          <div className="xfr-two">
            <div>
              <div className="xfr-label">{t("TIEMPO", "TIME")}</div>
              <div className="xfr-pills">
                {TIMES.map((value) => (
                  <button key={value} type="button" className={`xfr-pillbtn${minutes === value ? " is-active" : ""}`} onClick={() => setMinutes(value)}>
                    {value} min
                  </button>
                ))}
              </div>
            </div>
            {challengeType === "bet" ? (
              <div>
                <div className="xfr-label">
                  {t("APUESTA", "STAKE")} <small>· {t("tienes", "you have")} {formatNumber(walletBalance, language)} 🪙</small>
                </div>
                <div className="xfr-pills">
                  {stakes.map((value) => (
                    <button key={value} type="button" className={`xfr-pillbtn${stake === value ? " is-active" : ""}`} onClick={() => setStake(value)}>
                      {formatNumber(value, language)} 🪙
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {createdTournament ? (
        <div className="xfr-created">
          <b>{t("¡Mini torneo listo!", "Mini tournament ready!")}</b>
          <span>
            {t("Contraseña", "Password")}: <b>{createdTournament.password}</b> · {t("tus amigos entran sin escribirla al aceptar.", "friends join without typing it.")}
          </span>
          <button type="button" className="xpz-btn is-gold" onClick={() => navigate(`/game/${createdTournament.tournamentId}`)}>
            🏆 {t("Ir al torneo", "Go to the tournament")}
          </button>
        </div>
      ) : (
        <button type="button" className="xpz-btn is-gold is-large xfr-send" disabled={challengeDisabled} onClick={sendChallenge}>
          {busy === "challenge" ? t("Enviando…", "Sending…") : typeInfo.soon ? t("Muy pronto", "Coming soon") : sendLabel()}
        </button>
      )}
      <div className="xfr-note">{sendNote()}</div>
    </div>
  );

  const progress = Math.min(bonusInvites, Number(referral.paidCount) || 0);
  const renderInviteCard = () => (
    <div className="xfr-card xfr-invite" ref={inviteCardRef}>
      <div className="xfr-invite-head">
        <div>
          <div className="xfr-label is-gold">{t("INVITA Y GANA", "INVITE & EARN")}</div>
          <div className="xfr-invite-title">
            {t("Gana", "Earn")} <span className="xfr-coin">{formatNumber(inviterReward, language)} 🪙</span> {t("por cada amigo nuevo", "per new friend")}
          </div>
          <div className="xfr-muted">
            {t(
              `Se paga cuando tu amigo se registra con tu enlace y termina su primera partida. Él recibe ${formatNumber(inviteeReward, language)} 🪙 de bienvenida.`,
              `Paid when your friend signs up with your link and finishes their first game. They get ${inviteeReward} 🪙.`
            )}
          </div>
        </div>
        <div className="xfr-gift" aria-hidden="true">🎁</div>
      </div>
      {me?.isGuest ? (
        <div className="xfr-guest">{t("Crea tu cuenta para tener tu enlace de invitación y cobrar las monedas.", "Create your account to get your invite link.")}</div>
      ) : (
        <>
          <button type="button" className="xfr-link" onClick={() => copyText(inviteLink, t("Enlace copiado", "Link copied"))}>
            🔗 <span>{inviteLink.replace(/^https:\/\//, "")}</span>
            <em>{t("Copiar", "Copy")}</em>
          </button>
          <div className="xfr-share">
            <button type="button" className="xfr-share-btn is-wa" onClick={shareWhatsApp}>WhatsApp</button>
            <button type="button" className="xfr-share-btn is-ct" onClick={shareContacts}>📇 {t("Contactos", "Contacts")}</button>
            <button type="button" className="xfr-share-btn is-cp" onClick={shareEmail}>✉ {t("Correo", "Email")}</button>
          </div>
        </>
      )}
      <div className="xfr-progress-head">
        <span>
          <b>
            {progress} {t("de", "of")} {bonusInvites}
          </b>{" "}
          {t("amigos invitados", "friends invited")}
        </span>
        <span className="xfr-coin">
          {referral.bonusPaid
            ? t(`Bono de ${formatNumber(bonusReward, language)} 🪙 cobrado`, "Bonus collected")
            : t(`Bono extra a los ${bonusInvites}: ${formatNumber(bonusReward, language)} 🪙`, `Bonus at ${bonusInvites}: ${bonusReward} 🪙`)}
        </span>
      </div>
      <div className="xfr-bar">
        <i style={{ width: `${(progress / bonusInvites) * 100}%` }} />
      </div>
    </div>
  );

  const tabCount = { friends: friends.length, requests: requestsIncoming.length, challenges: receivedCount, invite: 0 };
  const tabLabel = {
    friends: t("Amigos", "Friends"),
    requests: t("Solicitudes", "Requests"),
    challenges: t("Retos recibidos", "Challenges"),
    invite: t("Invitar y ganar", "Invite & earn"),
  };

  return (
    <div className="xlm-root xpz-root xfr-root">
      <ModernSidebar language={language} active="friends" badges={outletContext.sidebarBadges || {}} isAdmin={isAdmin} actions={routeActions} />
      <div className="xlm-main xpz-main xfr-main">
        <div className="xpz-shell">
          <header className="xpz-top">
            <div className="xpz-title">
              <button type="button" className="xpz-back" onClick={() => navigate("/game/lobby")}>
                ‹ {t("Volver al Lobby", "Back to Lobby")}
              </button>
              <h1>{t("Amigos", "Friends")}</h1>
              <p>{t("Encuentra jugadores, haz amigos, lánzales retos e invita a gente nueva para ganar monedas.", "Find players, make friends, challenge them and invite new people to earn coins.")}</p>
            </div>
            <div className="xpz-kpis">
              <div className="xpz-kpi"><b>{formatNumber(kpis.friends, language)}</b><span>{t("Amigos", "Friends")}</span></div>
              <div className="xpz-kpi xfr-kpi-green"><b>{formatNumber(kpis.online, language)}</b><span>{t("En línea", "Online")}</span></div>
              <div className="xpz-kpi xfr-kpi-red"><b>{formatNumber(kpis.pendingRequests, language)}</b><span>{t("Solicitudes", "Requests")}</span></div>
              <div className="xpz-kpi is-gold"><b>{formatNumber(kpis.coinsEarned, language)} 🪙</b><span>{t("Ganadas invitando", "Earned inviting")}</span></div>
            </div>
          </header>

          <div className="xfr-card xfr-steps">
            {[
              [t("Busca", "Search"), t("Por su nombre o por su código XIMA.", "By name or XIMA code.")],
              [t("Agrega", "Add"), t("Cuando acepte, ya son amigos.", "Once accepted, you're friends.")],
              [t("Reta", "Challenge"), t("Elige el tipo de reto y el modo.", "Pick the challenge and mode.")],
              [t("Invita y gana", "Invite & earn"), t(`${formatNumber(inviterReward, language)} 🪙 por cada amigo nuevo que juegue.`, `${inviterReward} 🪙 per new friend who plays.`)],
            ].map(([title, desc], index) => (
              <div key={title} className="xfr-step">
                <div className="xfr-step-k">{index + 1}</div>
                <div>
                  <b>{title}</b>
                  <span>{desc}</span>
                </div>
              </div>
            ))}
          </div>

          {loadError && !hub ? (
            <div className="xfr-card xfr-empty">
              <b>{loadError}</b>
              <button type="button" className="xpz-btn is-gold" onClick={() => loadHub(false)}>⟳ {t("Reintentar", "Retry")}</button>
            </div>
          ) : (
            <div className="xfr-layout">
              <div className="xfr-left">
                {renderSearch()}
                <div className="xfr-tabs" role="tablist">
                  {TABS.map((id) => (
                    <button key={id} type="button" role="tab" aria-selected={tab === id} className={`xfr-tab${tab === id ? " is-active" : ""}`} onClick={() => setTab(id)}>
                      {tabLabel[id]}
                      {tabCount[id] ? <span className={`xfr-n${id === "friends" ? " is-gold" : ""}`}>{tabCount[id]}</span> : null}
                    </button>
                  ))}
                </div>
                <div className="xfr-list">
                  {loading && !hub ? <div className="xfr-empty is-small">{t("Cargando…", "Loading…")}</div> : null}
                  {hub && tab === "friends" ? renderFriends() : null}
                  {hub && tab === "requests" ? renderRequests() : null}
                  {hub && tab === "challenges" ? renderChallenges() : null}
                  {hub && tab === "invite" ? renderInviteList() : null}
                </div>
              </div>
              <div className="xfr-right">
                {renderPanel()}
                {renderInviteCard()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Friends;
